import { notification } from "antd";
import classNames from "classnames";
import AccessControl from "components/common/AccessControl/accessControl";
import FilterPopover from "components/common/FilterPopover/component";
import AddWorkspaceModal from "components/common/Modals/AddWorkspaceModal";
import EditWorkspaceModal from "components/common/Modals/EditWorkspaceModal";
import UserManagementModal from "components/common/Modals/UserManagementModal";
import SVG from "components/common/SVG";
import { allUserRoles, ENTITY_TYPE, ROLE } from "constants/phase";
import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { downloadOrganizationCSV } from "store/organization/duck";

import DeleteModal from "../../components/common/DeleteModal";
import AddUserModal from "../../components/common/Modals/AddUserModal";
import AssignUserModal from "../../components/common/Modals/AssignUserModal";
import { deleteWorkspace, getWorkspaceDetails, getWorkspaces } from "../../store/workspace/duck";
import { removeWorkspaceMember } from "../../store/workspaceMember/duck";
import { convertIsoDateToStandardFormat } from "../../utils/date";
import ActionsContainer from "./ActionsContainer";
import OrganizationList from "./OrganizationList";
import styles from "./style.module.scss";

//const mapStateToProps = () => { };

const mapDispatchToProps = {
  getWorkspaces,
  deleteWorkspace,
  removeWorkspaceMember,
  downloadOrganizationCSV,
  getWorkspaceDetails,
};

const pageSize = 10;

const WorkspaceManagement = ({
  getWorkspaces,
  deleteWorkspace,
  removeWorkspaceMember,
  getWorkspaceDetails,
  profileView = false,
  workspaceOrganization = null,
  downloadOrganizationCSV,
}) => {
  const [search, setSearch] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [workspaces, setWorkspaces] = useState([]);
  const [totalOrganizations, setTotalOrganizations] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(null);
  const [showEditWorkspaceModal, setShowEditWorkspaceModal] = useState(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showUserManagementModal, setShowUserManagementModal] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(null);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState([]);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);

  const { currentRole, entityId, entityType } = useOutletContext();
  const isAdmin = currentRole === allUserRoles.admin;
  const isUser = currentRole === allUserRoles.user;
  // const handlePageChange = (activePage) => {
  //   setActivePage(activePage);
  // };
  const handleRoleCellClick = useCallback(async ({ count, workspace, role }) => {
    const totalUserCount =
      (workspace?.userCounts?.admin ? workspace?.userCounts?.admin : 0) +
      (workspace?.userCounts?.reviewer ? workspace?.userCounts?.reviewer : 0) +
      (workspace?.userCounts?.user ? workspace?.userCounts?.user : 0);

    const { value } = await getWorkspaceDetails({
      entityId: workspace?.workspaceId,
      getConfig: true,
    });

    if (value?.status) {
      const maxNumberOfUsers = value?.organizationConfig?.maxNumberOfUsers
        ? value?.organizationConfig?.maxNumberOfUsers
        : 3;

      if (count && count > 0) {
        setShowUserManagementModal({ workspace, role, maxNumberOfUsers, totalUserCount });
        return;
      }
      if (maxNumberOfUsers > totalUserCount) {
        setShowAddUserModal(workspace);
      } else {
        notification.warning({
          message: `You can add only ${maxNumberOfUsers} Users.`,
        });
      }
    } else {
    }
  }, []);
  const navigate = useNavigate();

  const organizationRows = useMemo(() => {
    return workspaces.map((workspace) => {
      return {
        key: workspace.organizationId,
        name: (
          <div className={styles.profileContainer}>
            <div className={styles.logo}>
              <img className={styles.logoPreview} src={workspace?.logo} alt="profile-preview" />
            </div>
            <div className={styles.name}>{workspace?.displayName}</div>
          </div>
        ),
        createdAt: workspace.createdAt ? convertIsoDateToStandardFormat(workspace.createdAt) : "",
        admin: (
          <AccessControl code="admin_workspace" page="organization-profile">
            <p
              className={classNames(styles.userCount, {
                [styles.readOnlyCount]: isUser,
              })}
              onClick={() =>
                (currentRole === ROLE.USER || currentRole === ROLE.REVIEWER) && !+workspace?.userCounts?.admin
                  ? ""
                  : handleRoleCellClick({
                      workspace,
                      role: allUserRoles.admin,
                      count: +workspace?.userCounts?.admin || 0,
                    })
              }
            >
              {workspace.userCounts?.admin || 0}
            </p>
          </AccessControl>
        ),
        user: (
          <AccessControl code="user_workspace" page="organization-profile">
            <p
              className={classNames(styles.userCount, {
                [styles.readOnlyCount]: isUser,
              })}
              onClick={() =>
                !+workspace?.userCounts?.user && (currentRole === ROLE.USER || currentRole === ROLE.REVIEWER)
                  ? ""
                  : handleRoleCellClick({
                      workspace,
                      role: allUserRoles.user,
                      count: +workspace?.userCounts?.user || 0,
                    })
              }
            >
              {workspace.userCounts?.user || 0}
            </p>
          </AccessControl>
        ),
        reviewer: (
          <AccessControl code="reviewer_workspace" page="organization-profile">
            <p
              className={classNames(styles.userCount, {
                [styles.readOnlyCount]: isUser,
              })}
              onClick={() =>
                !+workspace?.userCounts?.reviewer && (currentRole === ROLE.USER || currentRole === ROLE.REVIEWER)
                  ? ""
                  : handleRoleCellClick({
                      workspace,
                      role: allUserRoles.reviewer,
                      count: +workspace?.userCounts?.reviewer || 0,
                    })
              }
            >
              {workspace.userCounts?.reviewer || 0}
            </p>
          </AccessControl>
        ),
        actions: (
          <AccessControl code="actions_workspace" page="organization-profile">
            <ActionsContainer
              workspace={workspace}
              setShowAssignUserModal={setShowAssignUserModal}
              setShowEditWorkspaceModal={setShowEditWorkspaceModal}
              setShowDeleteModal={setShowDeleteModal}
              downloadOrganizationCSV={downloadOrganizationCSV}
            />
          </AccessControl>
        ),
      };
    });
  }, [handleRoleCellClick, isAdmin, workspaces, isUser]);

  const fetchWorkspaces = useCallback(
    async ({ page = 1, search = "", dateRange = [] }) => {
      try {
        setLoading(true);
        const payload = {
          page,
          pageSize,
          search,
          fromDate: dateRange ? dateRange[0] : null,
          toDate: dateRange ? dateRange[1] : null,
        };

        if (isAdmin && entityType === ENTITY_TYPE.ORGANIZATION && entityId) {
          payload.organizationId = entityId;
        }

        if (workspaceOrganization) {
          // this is only true when coming from profile page
          payload.organizationId = workspaceOrganization?.organizationId;
        }

        const response = await getWorkspaces(payload);

        if (response?.value?.status) {
          setTotalOrganizations(response?.value?.total);
          setWorkspaces(response?.value?.workspaces);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [entityId, entityType, getWorkspaces, isAdmin, workspaceOrganization],
  );

  const handlePageChange = useCallback(
    (page) => {
      setActivePage(page);
      fetchWorkspaces({ page, search }); // Trigger immediate API call with current search value
    },
    [fetchWorkspaces, search],
  );

  const closeAndFetchEditModal = useCallback(() => {
    setShowEditWorkspaceModal(null);
    fetchWorkspaces({ page: activePage, search });
  }, [activePage, fetchWorkspaces, search]);

  const closeAndFetchAddModal = useCallback(() => {
    setShowAddWorkspaceModal(null);
    fetchWorkspaces({ page: activePage, search });
  }, [activePage, fetchWorkspaces, search]);

  const closeAndFetchAssignUserModal = useCallback(() => {
    setShowAssignUserModal(null);
    fetchWorkspaces({ page: activePage, search });
  }, [activePage, fetchWorkspaces, search]);

  const closeAndFetchUserManagementModal = useCallback(() => {
    setShowUserManagementModal(null);
    fetchWorkspaces({ page: activePage, search });
  }, [activePage, fetchWorkspaces, search]);

  const closeAndFetchAddUserModal = useCallback(() => {
    setShowAddUserModal(null);
    fetchWorkspaces({ page: activePage, search });
  }, [activePage, fetchWorkspaces, search]);

  const debouncedFetchWorkspaces = useMemo(
    () =>
      debounce((search) => {
        setActivePage(1);
        fetchWorkspaces({ page: 1, search });
      }, 800),
    [fetchWorkspaces],
  );

  // const onSearch = (e) => {
  //   setSearch(e.target.value);
  //   debouncedFetchWorkspaces(e.target.value);
  // };

  const onSearch = useCallback(
    (value) => {
      setSearch(value);
      setActivePage(1); // Reset activePage to 1 when search query changes
      debouncedFetchWorkspaces(value); // Trigger debounced API call with updated search value
    },
    [debouncedFetchWorkspaces],
  );

  const handleDelete = useCallback(
    async (workspaceId, search = "") => {
      try {
        setLoading(true);
        const payload = {
          workspaceId,
        };

        const response = await deleteWorkspace(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setShowDeleteModal(null);
        fetchWorkspaces({ page: activePage, search });
        setActivePage(1);
      }
    },
    [activePage, deleteWorkspace, fetchWorkspaces],
  );

  const handleRemoveUser = useCallback(
    async (workspaceMemberId, search = "") => {
      try {
        setLoading(true);
        const payload = {
          workspaceMemberId,
        };

        const response = await removeWorkspaceMember(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setShowRemoveUserModal(null);
        fetchWorkspaces({ page: activePage, search });
      }
    },
    [activePage, fetchWorkspaces, removeWorkspaceMember],
  );

  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.profileViewContainer]: profileView,
        })}
      >
        <div className={styles.headerContainer}>
          <div className={styles.leftCont}>
            <div className={styles.title}>Workspace management</div>
          </div>

          <div className={styles.rightCont}>
            <div className={styles.searchContainer}>
              <div className={styles.leftCont}>
                <SVG className={styles.filterIcon} iconName={"images/search.svg"} />
                <input
                  type="search"
                  name="org-search"
                  id="org-search"
                  placeholder="Search"
                  autoFocus
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                  className={styles.search}
                />
                <FilterPopover
                  dateRange={filterDateRange}
                  setDateRange={setFilterDateRange}
                  setIsModalOpen={setFilterPopupOpen}
                  fetch={fetchWorkspaces}
                  activePage={activePage}
                  isModalOpen={filterPopupOpen}
                  setActivePage={setActivePage}
                />
              </div>
            </div>

            {!isAdmin && (
              <AccessControl code="add_workspace" page="organization-profile">
                <div
                  className={styles.addOrganizationCta}
                  onClick={() =>
                    workspaceOrganization
                      ? setShowAddWorkspaceModal(workspaceOrganization)
                      : setShowAddWorkspaceModal(true)
                  }
                >
                  Add new workspace
                </div>
              </AccessControl>
            )}
          </div>
        </div>

        <OrganizationList
          activePage={activePage}
          organizationRows={organizationRows}
          totalCount={totalOrganizations}
          pageSize={pageSize}
          loading={loading}
          handlePageChange={handlePageChange}
          isAdmin={isAdmin}
        />
      </div>

      {showAddWorkspaceModal && (
        <AddWorkspaceModal
          showModal={showAddWorkspaceModal}
          closeAndFetchModal={closeAndFetchAddModal}
          setShowModal={setShowAddWorkspaceModal}
        />
      )}

      {showEditWorkspaceModal && (
        <EditWorkspaceModal
          workspace={showEditWorkspaceModal}
          closeAndFetchModal={closeAndFetchEditModal}
          setShowModal={setShowEditWorkspaceModal}
        />
      )}

      {showAssignUserModal && (
        <AssignUserModal
          entity={showAssignUserModal}
          entityType={ENTITY_TYPE.WORKSPACES}
          closeAndFetchModal={closeAndFetchAssignUserModal}
          setShowModal={setShowAssignUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          id={showDeleteModal?.workspaceId}
          setId={setShowDeleteModal}
          handleDelete={() => handleDelete(showDeleteModal?.workspaceId, search)}
          content={`Are you sure you want to delete the workspace - ${showDeleteModal?.name} ?`}
          showDeleteText
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          showModal={true}
          entity={showAddUserModal}
          entityType={ENTITY_TYPE.WORKSPACES}
          closeAndFetchModal={closeAndFetchAddUserModal}
          setShowModal={setShowAddUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showUserManagementModal && (
        <UserManagementModal
          setShowModal={setShowUserManagementModal}
          entity={showUserManagementModal?.workspace}
          entityType={ENTITY_TYPE.WORKSPACES}
          role={showUserManagementModal?.role}
          closeAndFetchModal={closeAndFetchUserManagementModal}
          setShowRemoveUserModal={setShowRemoveUserModal}
          setShowAddUserModal={setShowAddUserModal}
        />
      )}

      {showRemoveUserModal && (
        <DeleteModal
          id={showRemoveUserModal?.workspaceMemberId}
          setId={setShowRemoveUserModal}
          handleDelete={() => handleRemoveUser(showRemoveUserModal?.workspaceMemberId, search)}
          content={`Are you sure you want to remove the user - ${showRemoveUserModal?.name} from the workspace ?`}
        />
      )}
    </>
  );
};

export default connect((/*state*/) => ({}), mapDispatchToProps)(WorkspaceManagement);
